import type {
  BatchOp,
  BatchOpResponse,
  ReadyzResponse,
  TrashView,
  TriggerResponse,
} from '@/types/common';
import type { User } from '@/types/auth';
import type {
  ArchetypeOption,
  ArchetypeSlug,
  CharacterDraft,
  DisclosurePolicy,
  PaginatedPersonaResponse,
  Persona,
  PersonaBrief,
  TriggerPersonaInput,
} from '@/types/persona';
import type {
  CaptionPreview,
  Content,
  ContentPost,
  PaginatedContentResponse,
  PreviewPromptResponse,
  ProductImageUpload,
  SponsoredProductInput,
  TriggerContentInput,
} from '@/types/content';
import type {
  PaginatedWorkflowResponse,
  WorkflowDetail,
  WorkflowEvents,
  WorkflowExecution,
} from '@/types/run';
import type { Account, LinkAccountInput, LinkAccountResponse } from '@/types/account';
import type { StreamRequest, ThreadDetail, ThreadSummary } from '@/types/chat';
import type { ContentPerformance, PerformanceList } from '@/types/engagement';
import type { Competitor, ResearchOverview } from '@/types/research';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${BASE}${path}`, {
    ...init,
    // Send the httpOnly session cookie cross-origin (dashboard ↔ api subdomain).
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!r.ok) {
    let detail: string;
    try {
      const body = (await r.json()) as {
        detail?: unknown;
        error?: { message?: unknown };
      };
      if (typeof body.detail === 'string') {
        detail = body.detail;
      } else if (typeof body.error?.message === 'string') {
        // Domain-error envelope: { error: { code, message, details } }
        detail = body.error.message;
      } else {
        detail = JSON.stringify(body);
      }
    } catch {
      detail = await r.text();
    }
    throw new APIError(r.status, detail);
  }
  if (r.status === 204 || r.status === 205) return undefined as T;
  // Some endpoints return a success status with an empty body (e.g. the IG
  // code submit returns 202 no-content). Calling .json() on an empty body
  // throws "Unexpected end of JSON input" — which surfaced as a false
  // "Could not submit code" error even though the submit succeeded. Read the
  // body as text first and only parse when there's something to parse.
  const text = await r.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export class APIError extends Error {
  constructor(
    public status: number,
    public detail: string,
  ) {
    super(`HTTP ${status}: ${detail}`);
    this.name = 'APIError';
  }
}
async function readSse(
  url: string,
  body: string,
  onEvent: (e: { event: string; data: string }) => void,
  signal?: AbortSignal,
): Promise<void> {
  const init: RequestInit = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
  };
  if (body) init.body = body;
  if (signal) init.signal = signal;
  const r = await fetch(url, init);
  if (!r.ok || !r.body) {
    let detail: string;
    try {
      detail = await r.text();
    } catch {
      detail = r.statusText;
    }
    throw new APIError(r.status, detail);
  }
  const reader = r.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) return;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf('\n\n')) >= 0) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const evMatch = frame.match(/^event: (.+)$/m);
      const dataMatch = frame.match(/^data: (.+)$/m);
      if (evMatch?.[1] && dataMatch?.[1]) {
        onEvent({ event: evMatch[1], data: dataMatch[1] });
      }
    }
  }
}

export const api = {
  readyz: () => req<ReadyzResponse>('/readyz'),
  archetypes: () => req<ArchetypeOption[]>('/archetypes'),
  disclosurePolicy: () => req<DisclosurePolicy>('/archetypes/disclosure'),
  draftCharacter: (archetype: ArchetypeSlug) =>
    req<CharacterDraft>('/personas:draft-character', {
      method: 'POST',
      body: JSON.stringify({ archetype }),
    }),

  // ─── Auth ───────────────────────────────────────────────────────────
  auth: {
    // Session check — returns the user, or null (HTTP 200) when logged out.
    me: () => req<User | null>('/auth/me'),
    login: (body: { email: string; password: string }) =>
      req<User>('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
    // In-signup email verification (step 1 + 2).
    sendCode: (email: string) =>
      req<{ message: string }>('/auth/send-code', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
    verifyCode: (body: { email: string; code: string }) =>
      req<{ email_verification_token: string }>('/auth/verify-code', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    signup: (body: {
      email: string;
      password: string;
      email_verification_token: string;
      first_name?: string | undefined;
      last_name?: string | undefined;
    }) => req<User>('/auth/signup', { method: 'POST', body: JSON.stringify(body) }),
    logout: () => req<void>('/auth/logout', { method: 'POST' }),
    forgotPassword: (email: string) =>
      req<{ message: string }>('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
    resetPassword: (body: { token: string; password: string }) =>
      req<{ message: string }>('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },

  listPersonas: (params?: { limit?: number; offset?: number; view?: TrashView }) => {
    const qs = new URLSearchParams();
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    if (params?.offset !== undefined) qs.set('offset', String(params.offset));
    if (params?.view) qs.set('view', params.view);
    const q = qs.toString();
    return req<Persona[]>(`/personas${q ? `?${q}` : ''}`);
  },
  listPersonasPaginated: (params?: {
    q?: string;
    archetype?: string;
    platform?: string;
    page?: number;
    page_size?: number;
    view?: TrashView;
  }) => {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.archetype) qs.set('archetype', params.archetype);
    if (params?.platform) qs.set('platform', params.platform);
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.view) qs.set('view', params.view);
    const q = qs.toString();
    return req<PaginatedPersonaResponse>(`/personas:list${q ? `?${q}` : ''}`);
  },
  getPersona: (id: string) => req<Persona>(`/personas/${encodeURIComponent(id)}`),
  getPersonaBrief: (id: string) => req<PersonaBrief>(`/personas/${encodeURIComponent(id)}/brief`),
  listPersonaContent: (id: string) => req<Content[]>(`/personas/${encodeURIComponent(id)}/content`),
  triggerPersona: (input: TriggerPersonaInput) =>
    req<TriggerResponse>('/personas:trigger', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  deletePersona: (id: string, opts?: { purge?: boolean; cascade?: boolean }) => {
    const qs = new URLSearchParams();
    if (opts?.purge) qs.set('purge', 'true');
    if (opts?.cascade) qs.set('cascade', 'true');
    const q = qs.toString();
    return req<void>(`/personas/${encodeURIComponent(id)}${q ? `?${q}` : ''}`, {
      method: 'DELETE',
    });
  },
  restorePersona: (id: string) =>
    req<Persona>(`/personas/${encodeURIComponent(id)}:restore`, { method: 'POST' }),
  batchPersonas: (ids: string[], op: BatchOp) =>
    req<BatchOpResponse>('/personas:batch', {
      method: 'POST',
      body: JSON.stringify({ ids, op }),
    }),

  listContent: (params?: {
    persona_id?: string;
    limit?: number;
    offset?: number;
    view?: TrashView;
  }) => {
    const qs = new URLSearchParams();
    if (params?.persona_id) qs.set('persona_id', params.persona_id);
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    if (params?.offset !== undefined) qs.set('offset', String(params.offset));
    if (params?.view) qs.set('view', params.view);
    const q = qs.toString();
    return req<Content[]>(`/content${q ? `?${q}` : ''}`);
  },
  listContentPaginated: (params?: {
    q?: string;
    persona_id?: string;
    status?: string;
    sort_by?: string;
    sort_dir?: string;
    page?: number;
    page_size?: number;
    view?: TrashView;
  }) => {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.persona_id) qs.set('persona_id', params.persona_id);
    if (params?.status) qs.set('status', params.status);
    if (params?.sort_by) qs.set('sort_by', params.sort_by);
    if (params?.sort_dir) qs.set('sort_dir', params.sort_dir);
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    if (params?.view) qs.set('view', params.view);
    const q = qs.toString();
    return req<PaginatedContentResponse>(`/content:list${q ? `?${q}` : ''}`);
  },
  getContent: (id: string) => req<Content>(`/content/${encodeURIComponent(id)}`),
  getContentCaption: (id: string) =>
    req<CaptionPreview>(`/content/${encodeURIComponent(id)}/caption`),

  // ─── Competitor research ───────────────────────────────────────────
  getResearch: (personaId: string) =>
    req<ResearchOverview>(`/personas/${encodeURIComponent(personaId)}/research`),
  addCompetitors: (personaId: string, handles: string[]) =>
    req<Competitor[]>(`/personas/${encodeURIComponent(personaId)}/competitors`, {
      method: 'POST',
      body: JSON.stringify({ handles }),
    }),
  removeCompetitor: (personaId: string, competitorId: string) =>
    req<void>(
      `/personas/${encodeURIComponent(personaId)}/competitors/${encodeURIComponent(competitorId)}`,
      { method: 'DELETE' },
    ),
  triggerResearch: (personaId: string) =>
    req<TriggerResponse>(`/personas/${encodeURIComponent(personaId)}/research:trigger`, {
      method: 'POST',
    }),
  triggerContent: (input: TriggerContentInput) =>
    req<TriggerResponse>('/content:trigger', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  previewContentPrompt: (input: {
    persona_id: string;
    brief: string | null;
    pillar?: string | null;
    custom_rules: string[];
    product?: SponsoredProductInput | null;
  }) =>
    req<PreviewPromptResponse>('/content:preview-prompt', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  uploadProductImage: async (file: File) => {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () =>
        typeof reader.result === 'string'
          ? resolve(reader.result)
          : reject(new Error('Could not read the file'));
      reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'));
      reader.readAsDataURL(file);
    });
    return req<ProductImageUpload>('/content:upload-product-image', {
      method: 'POST',
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type,
        data_base64: dataUrl,
      }),
    });
  },
  deleteContent: (id: string, opts?: { purge?: boolean }) => {
    const qs = opts?.purge ? '?purge=true' : '';
    return req<void>(`/content/${encodeURIComponent(id)}${qs}`, { method: 'DELETE' });
  },
  restoreContent: (id: string) =>
    req<Content>(`/content/${encodeURIComponent(id)}:restore`, { method: 'POST' }),
  batchContent: (ids: string[], op: BatchOp) =>
    req<BatchOpResponse>('/content:batch', {
      method: 'POST',
      body: JSON.stringify({ ids, op }),
    }),
  approveContent: (id: string, body: { note?: string } = {}) =>
    req<Content>(`/content/${encodeURIComponent(id)}:approve`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  rejectContent: (id: string, body: { note?: string } = {}) =>
    req<Content>(`/content/${encodeURIComponent(id)}:reject`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  regenerateContent: (
    id: string,
    body: { brief?: string; custom_rules?: string[]; pillar?: string | null } = {},
  ) =>
    req<TriggerResponse>(`/content/${encodeURIComponent(id)}:regenerate`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  postContent: (id: string, body?: { account_id?: string; account_ids?: string[] }) =>
    req<TriggerResponse>(`/content/${encodeURIComponent(id)}:post`, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),
  getContentPosts: (id: string) => req<ContentPost[]>(`/content/${encodeURIComponent(id)}/posts`),

  listWorkflows: (params?: {
    type?: string;
    status?: string;
    last_hours?: number;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.type) qs.set('type', params.type);
    if (params?.status) qs.set('status', params.status);
    if (params?.last_hours !== undefined) qs.set('last_hours', String(params.last_hours));
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    const q = qs.toString();
    return req<WorkflowExecution[]>(`/workflows${q ? `?${q}` : ''}`);
  },
  listWorkflowsPaginated: (params?: {
    q?: string;
    type?: string;
    status?: string;
    last_hours?: number;
    page?: number;
    page_size?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.type) qs.set('type', params.type);
    if (params?.status) qs.set('status', params.status);
    if (params?.last_hours !== undefined) qs.set('last_hours', String(params.last_hours));
    if (params?.page !== undefined) qs.set('page', String(params.page));
    if (params?.page_size !== undefined) qs.set('page_size', String(params.page_size));
    const q = qs.toString();
    return req<PaginatedWorkflowResponse>(`/workflows:list${q ? `?${q}` : ''}`);
  },
  getWorkflow: (id: string) => req<WorkflowDetail>(`/workflows/${encodeURIComponent(id)}`),
  getWorkflowEvents: (id: string) =>
    req<WorkflowEvents>(`/workflows/${encodeURIComponent(id)}/events`),

  videoUrl: (contentId: string) => `${BASE}/media/video/${encodeURIComponent(contentId)}`,

  chat: {
    listThreads: (params?: { limit?: number; offset?: number }) => {
      const qs = new URLSearchParams();
      if (params?.limit !== undefined) qs.set('limit', String(params.limit));
      if (params?.offset !== undefined) qs.set('offset', String(params.offset));
      const q = qs.toString();
      return req<ThreadSummary[]>(`/chat/threads${q ? `?${q}` : ''}`);
    },
    getThread: (threadId: string) =>
      req<ThreadDetail>(`/chat/threads/${encodeURIComponent(threadId)}`),
    deleteThread: (threadId: string) =>
      req<void>(`/chat/threads/${encodeURIComponent(threadId)}`, { method: 'DELETE' }),
    stream: (
      req: StreamRequest,
      onEvent: (e: { event: string; data: string }) => void,
      signal?: AbortSignal,
    ) => readSse(`${BASE}/chat:stream`, JSON.stringify(req), onEvent, signal),
    approveTool: (
      threadId: string,
      toolUseId: string,
      onEvent: (e: { event: string; data: string }) => void,
      signal?: AbortSignal,
      customRules?: string[],
      sceneMode?: boolean,
    ) =>
      readSse(
        `${BASE}/chat/threads/${encodeURIComponent(threadId)}/tools/${encodeURIComponent(
          toolUseId,
        )}:approve`,
        // Operator's "Customize" choices ride along with the approval:
        // custom_rules append to the script prompt; scene_mode composes a
        // face-consistent establishing frame so scenery doesn't pop in.
        JSON.stringify({ custom_rules: customRules ?? [], scene_mode: sceneMode ?? false }),
        onEvent,
        signal,
      ),
    cancelTool: (threadId: string, toolUseId: string) =>
      req<void>(
        `/chat/threads/${encodeURIComponent(threadId)}/tools/${encodeURIComponent(
          toolUseId,
        )}:cancel`,
        { method: 'POST' },
      ),
  },

  listPersonaAccounts: (personaId: string) =>
    req<Account[]>(`/personas/${encodeURIComponent(personaId)}/accounts`),
  linkAccount: (personaId: string, input: LinkAccountInput) =>
    req<LinkAccountResponse>(`/personas/${encodeURIComponent(personaId)}/accounts:link`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  validateAccount: (accountId: string) =>
    req<TriggerResponse>(`/accounts/${encodeURIComponent(accountId)}:validate`, {
      method: 'POST',
    }),
  reconnectAccount: (accountId: string) =>
    req<TriggerResponse>(`/accounts/${encodeURIComponent(accountId)}:reconnect`, {
      method: 'POST',
    }),
  connectAccount: (
    accountId: string,
    credentials: { username: string; password?: string; cookies?: Record<string, unknown>[] },
  ) =>
    req<TriggerResponse>(`/accounts/${encodeURIComponent(accountId)}:connect`, {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),
  disconnectAccount: (accountId: string) =>
    req<Account>(`/accounts/${encodeURIComponent(accountId)}:disconnect`, {
      method: 'POST',
    }),
  submitLoginCode: (accountId: string, code: string) =>
    req<void>(`/accounts/${encodeURIComponent(accountId)}:submit-code`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  syncAccountProfile: (accountId: string) =>
    req<TriggerResponse>(`/accounts/${encodeURIComponent(accountId)}:sync-profile`, {
      method: 'POST',
      body: JSON.stringify({ name: true, bio: true, avatar: true }),
    }),
  refreshAccountStats: (accountId: string) =>
    req<TriggerResponse>(`/accounts/${encodeURIComponent(accountId)}:refresh-stats`, {
      method: 'POST',
    }),
  deleteAccount: (accountId: string) =>
    req<void>(`/accounts/${encodeURIComponent(accountId)}`, { method: 'DELETE' }),

  fetchPerformance: (params?: { persona_id?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.persona_id) qs.set('persona_id', params.persona_id);
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    if (params?.offset !== undefined) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return req<PerformanceList>(`/performance${query ? `?${query}` : ''}`);
  },
  fetchContentPerformance: (contentId: string) =>
    req<ContentPerformance>(`/performance/${encodeURIComponent(contentId)}`),
};
